import { v1 as uuid } from 'uuid';
import {
  map,
  omitBy,
  pick,
  filter,
  isUndefined,
  some,
  sortBy,
  every,
  find,
  get,
  trim,
} from 'lodash';

import { hasMissingQuota, hasWrongQuotaSum } from 'utils/quotaHelpers';
import {
  AGE_LIMITS,
  AGE_LIMITS_HIDE_UNDER_EIGHTEEN,
  AGE_RANGE_MIN_DIFFERENCE_CONSTRAINT,
} from './constants';

const isOverlap = (rangeA, rangeB) =>
  rangeA.min <= rangeB.max && rangeA.max >= rangeB.min;

const isIdenticalRanges = (rangeA, rangeB) =>
  rangeA.min === rangeB.min && rangeA.max === rangeB.max;

export const getActiveValues = (values, isDefault) => {
  let activeValues = null;
  if (isDefault) {
    activeValues = filter(values, 'enabled');
  } else {
    activeValues = values;
  }
  return activeValues;
};

export const defaultAges = (hideUnderEighteenValues) => {
  const ages = [
    { value: '16 - 17', label: '16 - 17', min: 16, max: 17, refId: uuid() },
    { value: '18 - 24', label: '18 - 24', min: 18, max: 24, refId: uuid() },
    { value: '25 - 34', label: '25 - 34', min: 25, max: 34, refId: uuid() },
    { value: '35 - 44', label: '35 - 44', min: 35, max: 44, refId: uuid() },
    { value: '45 - 54', label: '45 - 54', min: 45, max: 54, refId: uuid() },
    {
      value: '> 54',
      label: '55+',
      min: 55,
      max: AGE_LIMITS.MAX,
      refId: uuid(),
    },
  ];

  return hideUnderEighteenValues ? ages.slice(1) : ages;
};

export const specificAges = (hideUnderEighteenValues) => {
  if (hideUnderEighteenValues) {
    return [
      { value: '18 - 24', label: '18 - 24', min: 18, max: 24, refId: uuid() },
    ];
  }

  return [
    { value: '16 - 17', label: '16 - 17', min: 16, max: 17, refId: uuid() },
  ];
};

function isUnderEighteen({ value }) {
  return value.includes('16') || value.includes('17');
}

export function hasUnderEighteenValue(ages) {
  return ages.some(isUnderEighteen);
}

export function filterAgesUnderEighteen(ages) {
  return ages.filter((age) => !isUnderEighteen(age));
}

export const canDisableValues = (values, isDefault) =>
  getActiveValues(values, isDefault).length > 1;

export const canHaveQuota = (values, isDefault) => {
  const activeValues = getActiveValues(values, isDefault);
  return activeValues.length > 1 && activeValues.length < 101;
};

export const getTransformedData = (data, properties) => {
  const { hasQuotas, values, isDefault } = data;
  const activeValues = getActiveValues(values, isDefault);
  const cleanValues = map(activeValues, (age) =>
    omitBy(pick(age, properties), isUndefined),
  );

  return { values: cleanValues, hasQuotas };
};

export const valueToMinMax = (
  value,
  minAge = AGE_LIMITS.MIN,
  maxAge = AGE_LIMITS.MAX,
) => {
  let min;
  let max;

  if (value.includes('>')) {
    // eslint-disable-next-line no-unused-vars
    const minLimit = value.split('>').map(trim).map(Number)[1];
    [min, max] = [minLimit + 1, maxAge];
  } else {
    [min, max] = value.split(/\s*-\s*/).map(Number);
  }
  return {
    min: Math.max(min || minAge, minAge),
    max: Math.min(max || maxAge, maxAge),
    original: {
      min,
      max,
    },
  };
};
export const minMaxToValue = (min, max) => `${min} - ${max}`;

export const getValidationErrors = (formValues, completes, isUnderEdit) => {
  const { values, isDefault, hasQuotas } = formValues;
  const activeValues = getActiveValues(values, isDefault);
  const errors = {};

  if (!activeValues.length) {
    errors.noValueSelected = true;
  }

  const anyRangeWithWrongDifference = some(
    activeValues,
    (range) => range.max - range.min < AGE_RANGE_MIN_DIFFERENCE_CONSTRAINT,
  );
  if (anyRangeWithWrongDifference) {
    errors.rangeDifference = true;
  }

  const anyRangeWithWrongOrder = some(
    activeValues,
    (range) => range.max < range.min,
  );
  if (anyRangeWithWrongOrder) {
    errors.rangeOrder = true;
  }

  const anyRangeOverlaps = some(activeValues, (rangeA, indexA) =>
    some(
      activeValues,
      (rangeB, indexB) => indexA !== indexB && isOverlap(rangeA, rangeB),
    ),
  );

  if (anyRangeOverlaps) {
    errors.rangeOverlap = true;
  }

  const hasIdenticalRanges = some(activeValues, (rangeA, indexA) =>
    some(
      activeValues,
      (rangeB, indexB) =>
        indexA !== indexB && isIdenticalRanges(rangeA, rangeB),
    ),
  );

  if (hasIdenticalRanges) {
    errors.identicalRanges = true;
  }

  if (hasQuotas) {
    const anyRangeWithoutQuota = hasMissingQuota(activeValues, isUnderEdit);
    if (anyRangeWithoutQuota) {
      errors.quotaMissing = true;
    }

    const quotaSumIsNot100 = hasWrongQuotaSum(activeValues, completes);
    if (quotaSumIsNot100) {
      errors.quotaSumError = true;
    }
  }
  return errors;
};

export const getInitializedQuota = (values, isDefault, enabled, completes) => {
  if (!enabled) return values;
  const activeValues = getActiveValues(values, isDefault);
  let updatedValues = null;

  const quotaIsNotInitialized = every(
    activeValues,
    (age) => get(age, 'quotaCompletes', false) === false,
  );

  if (quotaIsNotInitialized) {
    const count = activeValues.length;

    const itemQuotaCompletes = Math.floor(completes / count);
    const lastItemQuotaCompletes = completes - itemQuotaCompletes * (count - 1);
    const lastItemId = activeValues[count - 1].refId;

    updatedValues = values.map((age) => {
      const isLastItem = age.refId === lastItemId;
      const isActiveItem = find(
        activeValues,
        ({ refId }) => age.refId === refId,
      );

      const quotaCompletes = isLastItem
        ? lastItemQuotaCompletes
        : itemQuotaCompletes;

      if (isActiveItem) {
        return {
          ...age,
          quotaCompletes,
        };
      }

      return age;
    });
    return updatedValues;
  }
  return values;
};

export const isDefaultRanges = (values, hideUnderEighteenValues) => {
  const defaultValues = defaultAges(hideUnderEighteenValues);
  return every(values, (age) => some(defaultValues, ['value', age.value]));
};

export const prepareValues = (
  values = [],
  isDefault = true,
  hideUnderEighteenValues = false,
  ordered = true,
) => {
  const defaultValues = defaultAges(hideUnderEighteenValues);
  const ageLimits = hideUnderEighteenValues
    ? AGE_LIMITS_HIDE_UNDER_EIGHTEEN
    : AGE_LIMITS;
  values.forEach((age) => {
    const found = defaultValues.find(({ value }) => age.value === value);
    if (found) {
      found.enabled = true;
      found.quotaCompletes = age.quotaCompletes;
      found.id = age.id;
      found.refId = age.refId;
    }
  });

  let valuesToUse = isDefault ? defaultValues : values;

  valuesToUse = map(valuesToUse, (age) => {
    const { min, max, original } = isDefault
      ? age
      : valueToMinMax(age.value, ageLimits.MIN, ageLimits.MAX);
    let adjusted = false;
    const refId = age.refId || uuid();
    let { label, value } = age;
    if (!isDefault && (original.min !== min || original.max !== max)) {
      adjusted = true;
      value = minMaxToValue(min, max);
    }
    if (!isDefault) {
      label = minMaxToValue(min, max);
    }

    return {
      ...age,
      value,
      label,
      min,
      max,
      refId,
      adjusted,
    };
  });

  return ordered ? sortBy(valuesToUse, 'min') : valuesToUse;
};

export const hasAdjustedAges = (values) => some(values, 'adjusted');
